import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import 'react-circular-progressbar/dist/styles.css';
import { withTranslation } from "react-i18next";
import { BiExport, BiInfoCircle } from "react-icons/bi";
import { useParams, useSearchParams } from 'react-router-dom';
import { RegulationContext } from "../../../App";
import ReportingPeriod from "../../../components/Regulation/ReportingPeriod";
import { AnalyticsReportType, LS_AUTHORITY_KEY } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import ProfileSettingsService from "../../../core/service/ProfileSettingsService";
import Utils from "../../../core/utilities/Utils";
import "./AnalyticsReport.scss";


const AnalyticsReport = ({ t }) => {
	const ls = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));

	const [searchParams] = useSearchParams();

	const { energyKey } = useParams();
	const { metric_id } = useParams();
	const { type_id } = useParams();
    const fromDate = searchParams.get('from') ?? null;
	const toDate = searchParams.get('to') ?? null;

	const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
	const month = Utils.getMonthFromFrequency(glbRegulation.frequency);
	const year = new Date().getFullYear();
	const isEnergyProducedReport = energyKey === "EnergyProduced";
	const companyId = ls.profile.company.id;

	const [maintabledata, setMainTableData] = useState([]);
	const [totalConsumption, setTotalConsumption] = useState(null);
	const [totalBranchValue, setTotalBranchValue] = useState({});
	const [totalDataPoints, setTotalDataPoints] = useState([]);
	const [filter, setFilter] = useState({
		periodStart: fromDate ? fromDate : `${year}-${month}-01`,
		periodEnd: toDate ? toDate : `${year}-${month}-01`,
		branch: 'all'
	});
	const [branches, setBranches] = useState([]);

	useEffect(() => {
		if (!companyId) return;

		getBranches();
	}, [companyId]);

	useEffect(() => {
		if (!glbRegulation.id) return;

		getMetricDataReport();
	}, [glbRegulation.id]);

	const renderTooltipCustom = (data) => {
		const activityData = data.activity;
		return (
			<Tooltip className="mytooltip">
				<div><b>Source</b> : {activityData.source}</div>
				<div><b>Last Modified by</b> : {activityData.lastModifiedBy}</div>
				<div><b>Date</b> : {moment(activityData.lastModifiedDate).format("MMM Do YYYY, h:mm a")}</div>
			</Tooltip>
		);
	}

	const getMetricDataReport = () => {
		let req;
		if (+type_id > 0) {
			req = KPIService.getAnalyticsData(
				glbRegulation.id,
				getTypeCode(),
				filter.periodStart,
				filter.periodEnd
			);
		} else if (isEnergyProducedReport) {
			const params = {
				key: energyKey ?? '',
				periodfrom: filter.periodStart,
				periodto: filter.periodEnd,
				branch: filter.branch
			};
			req = KPIService.getCombinedMetricDataByRange(
				glbRegulation.id,
				metric_id,
				params
			);
		} else {
			req = KPIService.getMetricDataReport(
				glbRegulation.id,
				metric_id,
				filter.periodStart,
				energyKey ?? ''
			);
		}
		req?.then((response) => {
			if (response.status !== 200) return;

			if (+type_id > 0) {
				setMainTableData(response.data.data);
			} else {
				setTotalConsumption(response.data.totalBranchValue.Combined);
				setMainTableData(response.data.dataPoints);
				setTotalDataPoints(response.data.dataPoints);
				setTotalBranchValue(response.data.totalBranchValue);
				const newArray = Object.entries(response.data.totalBranchValue)
					.map(([name, value]) => ({
						name,
						value
					}));
				const orderedArray = response.data.dataPoints[0]?.branches?.map((item) =>
					newArray.find((elem) => elem.name === item.name)
				);
				const footerRow = {
					id: 0,
					name: 'Total Value',
					unit: "",
					combined: response.data.totalBranchValue?.Combined,
					branches: orderedArray
				};
				const totalData = response.data.dataPoints?.concat(footerRow);
				setMainTableData(totalData);
			}
		});
	}

	const getBranches = () => {
		ProfileSettingsService.getBranches(companyId)
			.then((res) => {
				if (res.status !== 200) return;

				setBranches(res.data.body);
			})
			.catch((error) => { });
	};

	const exportReport = () => {
		const params = {
			key: energyKey ?? '',
			periodfrom: filter.periodStart,
			periodto: isEnergyProducedReport ? filter.periodEnd : null,
			branch: isEnergyProducedReport ? filter.branch : null
		};
		KPIService.exportAnalyticReport(glbRegulation.id, metric_id, params)
			.then((response) => {
				let blob = new Blob([response.data], { type: 'application/octet-stream' });
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				link.download = `${energyKey}Report.xlsx`;
				link.click();
				link.remove();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const filterToRender = () => {
		return (

			<div className="row Content_Align mx-0 mb-2 align-items-end">
				<div className="col-3 p-2">
					<label className="mb-1">
						{isEnergyProducedReport || +type_id > 0
							? t("From.label")
							: t("ReportingPeriod.label")
						} <span className="text-red">*</span>
					</label>
					<ReportingPeriod 
						defaultPeriod={filter.periodStart}
						valueChangeCallback={(period) =>
							setFilter({ ...filter, periodStart: `${period.year}-${period.month}-01` })
						} />
				</div>
				{
					(isEnergyProducedReport || +type_id > 0) &&
					<>
						<div className="col-3 p-2">
							<label className="mb-1">
								{t("To.label")} <span className="text-red">*</span>
							</label>
							<ReportingPeriod 
								defaultPeriod={filter.periodEnd}
								valueChangeCallback={(period) =>
									setFilter({ ...filter, periodEnd: `${period.year}-${period.month}-01` })
								} />
						</div>
						<div className="col-3 p-2">
							<label className="mb-1">
								{t("Location.label")}
							</label>
							<select className="form-select"
								onChange={(e) => {
									setFilter({ ...filter, branch: e.target.value });
								}}
								value={filter.branch}>
								<option value='all' key='all'>{t("All.label")}</option>
								{
									branches?.map((b, index) => {
										return <option value={b.branchId} key={b.branchId}>{b.name}</option>
									})
								}
							</select>
						</div>
					</>
				}
				<div className="col-3 p-2">
					<button onClick={() => getMetricDataReport()} className="btn btn-primary">Apply</button>
					<button
						onClick={() => exportReport()}
						className="btn btn-secondary mx-2">
						<BiExport/> {t("Export.label")}
					</button>
				</div>
			</div>
		);
	};

	const getTypeCode = () => {
		const keys = Object.keys(AnalyticsReportType);
		let code;
		for (let index = 0; index < keys.length; index++) {
			const key = keys[index];
			if (AnalyticsReportType[key].id !== +type_id) continue;

			code = AnalyticsReportType[key].code;
			break;
		}
		return code ?? '';
	};

	return (
		<div className="AnalyticsReport">
			{/* <div className="List_header">
				<Breadcrumb className="h5">
					<Breadcrumb.Item onClick={() => { }} > {t("Analytics.label")}</Breadcrumb.Item>
					<Breadcrumb.Item active> </Breadcrumb.Item>
				</Breadcrumb>
			</div> */}
			{filterToRender()}
			<div className="List_table-card01 ">
				{maintabledata?.length > 0 && <Table responsive className='m-0 Datapoint_table' bordered>
					<thead>
						<tr key='dataPointHeader'>
							<th key='dpDPHeader'>{t('DataPoint.label')}</th>
							<th key='dpUnitHeader'>{t('Unit.label')}</th>
							<th key='dpCombinedHeader'>{t('Combined.label')}</th>
							<>{maintabledata?.length > 0 && maintabledata[0]?.branches?.map((item, index) => (
								<th key={`dpBranch-${index}-Header`}>{item.name}</th>
							))}</>
						</tr>
					</thead>
					<tbody>
						{maintabledata?.length > 0 && maintabledata?.map((item, index) => (
							<tr key={`dataPointKey-${index}`}>
								<td>{item.name} ({item.dataPointCode ?? item.code})</td>
								<td>{item.unit ?? item.uom}</td>
								<td>{item.combined ?? item.value}</td>
								<>
									{item.branches?.length > 0 && item.branches?.map((britem, brindex) => (
										<td>
											<>
												<div className='datavalue_info_tool'>
													{britem.value}
													{britem.value && britem.activity && <OverlayTrigger
														placement="top"
														overlay={renderTooltipCustom(britem)}
													><span className="Info_dataView"><BiInfoCircle /></span></OverlayTrigger>}
												</div>
											</>
										</td>
									))}
								</>
							</tr>
						))}
					</tbody>
				</Table>}
			</div>
		</div>
	);
};

export default withTranslation()(AnalyticsReport);

